import {
  configuredString,
  type KioskPrinterConfig,
  resolveKioskPrinterConfig,
} from '@/kiosk-core/config/kioskRuntimeConfig';
import {
  createKioskPrintingRuntime,
  type KioskPrintingRuntimeConfig,
} from '@/kiosk-core/printing/printingRuntime';
import {
  createStoragePrintQueueStore,
  type PrintJobStatusResult,
  type PrintJobSubmission,
  type PrintQueueJob,
  type PrintQueueStorage,
} from '@/kiosk-core/printing/printQueue';
import { createApiClient, unwrap } from '@/services/api/client';
import type { components } from '@/services/api/v1.gen';
import { delay } from '@/services/fetchDriver';
import { getHttpErrorTelemetry } from '@/services/http';
import { getKioskSlug, getRuntimePrinter } from '@/services/urlState';
import { addBreadcrumb, captureException } from '@/telemetry/sentry';
import { getEntrRequestHeaders } from './requestHeaders';
import type {
  EntrPrintTransport,
  PrintSelectionInput,
  PrintSelectionResult,
} from './types';

export interface KioskPrintTransportOptions {
  fetchImpl?: typeof fetch;
  apiBaseUrl?: string;
  apiKey?: string | null;
  apiKeyHeader?: string;
  printerId?: string | null;
  resolvePrinterId?: () => string | null | undefined;
  resolvePrinterConfig?: () => KioskPrinterConfig | null | undefined;
  verticalPrinterId?: string;
  horizontalPrinterId?: string;
  printPollIntervalMs?: number;
  maxPrintPolls?: number;
  printPollDeadlineMs?: number;
  printBatchRecoveryTtlMs?: number;
  printQueueNow?: () => number;
  printQueueStorage?: PrintQueueStorage;
}

const PRINT_QUEUE_STORAGE_KEY = 'entr_kiosk.print_queue';

export interface SubmitPrintJobRequestInput {
  sessionId: string;
  imageId: string;
  printerId: string;
  idempotencyKey?: string | null;
  // What the copy cost on its payment, in minor units; 0 is a copy without a
  // price. Absent: a caller from before the copy payment.
  amountCents?: number | null;
  paymentIntentId?: string | null;
  paymentSessionId?: string | null;
}

const STRIPE_PAYMENT_INTENT = /^pi_[A-Za-z0-9_]+$/;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// bucket_name is intentionally omitted — the worker derives the bucket.
//
// A paid copy names its Stripe payment intent and the session that holds it
// (the real session of the group: the copy prints under its synthetic id).
// The backend refunds that amount when the copy does not print. A payment id
// that is not a Stripe payment intent (the mock transport) is no payment.
export function buildSubmitPrintJobBody(
  input: SubmitPrintJobRequestInput
): components['schemas']['SubmitPrintJobPayload'] {
  const body: components['schemas']['SubmitPrintJobPayload'] = {
    session_id: input.sessionId,
    image_id: input.imageId,
    printer_name: input.printerId,
  };
  if (input.idempotencyKey) body.idempotency_key = input.idempotencyKey;
  if (typeof input.amountCents !== 'number') return body;

  const paid =
    input.amountCents > 0 &&
    !!input.paymentIntentId &&
    STRIPE_PAYMENT_INTENT.test(input.paymentIntentId);
  body.amount_cents = paid ? input.amountCents : 0;
  if (paid && input.paymentIntentId) {
    body.payment_intent_id = input.paymentIntentId;
    if (input.paymentSessionId && UUID.test(input.paymentSessionId)) {
      body.payment_session_id = input.paymentSessionId;
    }
  }
  return body;
}

export function resolveKioskPrintConfig(
  options: KioskPrintTransportOptions
): KioskPrinterConfig | null {
  // The gate and resolveKioskPrinterConfig use configuredString on the two
  // ids. Thus, in the gate, resolveKioskPrinterConfig does not read
  // options.printerId.
  if (
    configuredString(options.verticalPrinterId) ||
    configuredString(options.horizontalPrinterId)
  ) {
    return resolveKioskPrinterConfig(options);
  }

  // ?printer=… in the launch URL wins over the kiosks row. Same venue,
  // multiple physical printers per kiosk — the kiosk row carries one
  // canonical id, ops retarget per machine via the URL param.
  const urlPrinter = configuredString(getRuntimePrinter());
  if (urlPrinter) {
    return resolveKioskPrinterConfig({ printerId: urlPrinter });
  }

  if (options.resolvePrinterConfig) {
    const resolvedPrinterConfig = options.resolvePrinterConfig();
    // Pass only the two ids. If configuredString gives null for the two ids,
    // resolveKioskPrinterConfig reads printerId. A printerId key on this object
    // must not become the printer.
    if (resolvedPrinterConfig) {
      return resolveKioskPrinterConfig({
        verticalPrinterId: resolvedPrinterConfig.verticalPrinterId,
        horizontalPrinterId: resolvedPrinterConfig.horizontalPrinterId,
      });
    }
  }

  const sharedPrinter =
    configuredString(options.printerId) ??
    configuredString(options.resolvePrinterId?.()) ??
    configuredString(import.meta.env.VITE_ENTR_PRINT_PRINTER_ID);
  if (sharedPrinter)
    return resolveKioskPrinterConfig({ printerId: sharedPrinter });

  return resolveKioskPrinterConfig({
    verticalPrinterId: import.meta.env.VITE_ENTR_PRINT_VERTICAL_PRINTER_ID,
    horizontalPrinterId: import.meta.env.VITE_ENTR_PRINT_HORIZONTAL_PRINTER_ID,
  });
}

function getPrintRequestHeaders(
  options: KioskPrintTransportOptions
): Record<string, string> {
  return getEntrRequestHeaders({
    apiKey: options.apiKey,
    apiKeyHeader: options.apiKeyHeader,
    envApiKey: import.meta.env.VITE_ENTR_PRINT_API_KEY,
    envApiKeyHeader: import.meta.env.VITE_ENTR_PRINT_API_KEY_HEADER,
  });
}

function summarizePrintInput(
  input: PrintSelectionInput
): Record<string, unknown> {
  return {
    session_id: input.sessionId,
    selected_items: input.items.length,
    selected_copies: input.items.reduce((sum, item) => sum + item.quantity, 0),
    idempotency_key: input.idempotencyKey,
  };
}

function getLocalStorage(): Storage | undefined {
  try {
    return window.localStorage;
  } catch {
    return undefined;
  }
}

export function createKioskPrintTransport(
  options: KioskPrintTransportOptions = {}
): EntrPrintTransport {
  const api = createApiClient({
    baseUrl:
      options.apiBaseUrl ??
      import.meta.env.VITE_ENTR_PRINT_API_BASE_URL ??
      import.meta.env.VITE_KIOSK_API_BASE_URL ??
      '',
    fetchImpl: options.fetchImpl,
    headers: () => getPrintRequestHeaders(options),
  });
  const runtime = createKioskPrintingRuntime(
    {
      storage:
        options.printQueueStorage ??
        createStoragePrintQueueStore(
          getLocalStorage(),
          PRINT_QUEUE_STORAGE_KEY
        ),
      wait: (ms, signal) => delay(ms, signal, 'Print selection aborted'),
      observer: {
        recordEvent: addBreadcrumb,
      },
      async submitPrintJob(job, signal): Promise<PrintJobSubmission> {
        if (!job.imageUuid) {
          throw new Error('Missing image uuid');
        }
        const accepted = await unwrap(
          api.POST('/v1/print-jobs', {
            body: buildSubmitPrintJobBody({
              sessionId: job.wireSessionId,
              imageId: job.imageUuid,
              printerId: job.printerId,
              idempotencyKey: job.idempotencyKey,
              amountCents: job.amountCents,
              paymentIntentId: job.paymentIntentId,
              paymentSessionId: job.paymentSessionId,
            }),
            ...api.transport({ maxRetries: 2, signal }),
          })
        );
        return { jobKey: accepted.job_id };
      },
      async pollPrintJob(
        submission,
        _job,
        signal
      ): Promise<PrintJobStatusResult> {
        const status = await unwrap(
          api.GET('/v1/print-jobs/{job_key}', {
            params: { path: { job_key: submission.jobKey } },
            ...api.transport({ signal }),
          })
        );
        return {
          status: status.status,
          isTerminal: status.is_final,
          error: status.error,
          copyState: status.copy_state ?? null,
          refundAmountCents: status.refund_amount_cents ?? null,
          // Undefined stays undefined: an older backend does not send it.
          finalBy: status.final_by,
        };
      },
      onPrintRequestError(error, job) {
        handlePrintRequestError(error, job);
      },
    },
    {
      pollIntervalMs: options.printPollIntervalMs,
      maxPolls: options.maxPrintPolls,
      pollDeadlineMs: options.printPollDeadlineMs,
      batchRecoveryTtlMs: options.printBatchRecoveryTtlMs,
      now: options.printQueueNow,
    }
  );

  return {
    async printSelection(input): Promise<PrintSelectionResult> {
      const printConfig = resolveKioskPrintConfig(options);
      const runtimeConfig: KioskPrintingRuntimeConfig = {
        kioskSlug: getKioskSlug(),
        printer: printConfig,
      };
      runtime.initialize(runtimeConfig);
      addBreadcrumb('entr.print', 'print selection requested', {
        ...summarizePrintInput(input),
        printer_vertical: printConfig?.verticalPrinterId ?? null,
        printer_horizontal: printConfig?.horizontalPrinterId ?? null,
      });

      const result = await runtime.printSelection(input);

      if (result.ok) {
        addBreadcrumb('entr.print', 'print selection submitted', {
          printed_count: result.value.printedCount,
          job_count: result.value.jobId.split(',').filter(Boolean).length,
        });
      }

      return result;
    },
  };

  function handlePrintRequestError(error: unknown, job: PrintQueueJob): void {
    captureException(error, 'print_jobs_submit_print_job');
    addBreadcrumb('entr.print', 'print request failed', {
      // Full image UUID is intentional Sentry metadata for print-job joins.
      image_uuid: job.imageUuid,
      printer_id: job.printerId,
      idempotency_key: job.idempotencyKey,
      ...getHttpErrorTelemetry(error),
    });
  }
}
